import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectTransactionBatches, selectUploadIndicatorBatches } from 'store/storeTransactionImage/storeTransactionImageSelector';
import TransactionItem from './TransactionItem';
import WarningIcon from 'assets/icons/WarningIcon';

import PropTypes from 'prop-types';
import styles from './UploadNotification.module.css';

const AUTO_HIDE_DELAY = 1000;

const UploadNotification = ({ isOpen = false, defaultState = true, onClose, triggerRef }) => {
    // agent snapshots uploaded by the browser + Macaw images uploaded by the backend
    const batches = useSelector(selectUploadIndicatorBatches);
    const browserBatches = useSelector(selectTransactionBatches);
    const [hiddenTxIds, setHiddenTxIds] = useState(new Set());
    const timersRef = useRef(new Map());
    const hiddenTxIdsRef = useRef(hiddenTxIds);
    const panelRef = useRef(null);

    useEffect(() => {
        hiddenTxIdsRef.current = hiddenTxIds;
    }, [hiddenTxIds]);

    const handleClickOutside = useCallback(
        (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target) && triggerRef?.current && !triggerRef.current.contains(event.target)) {
                onClose?.();
            }
        },
        [onClose, triggerRef]
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen, handleClickOutside]);

    const groupedTransactions = useMemo(() => {
        if (!batches || batches.length === 0) return [];

        const groupedMap = batches.reduce((acc, batch) => {
            const txId = batch.transactionId;
            if (!acc[txId]) {
                acc[txId] = {
                    transactionId: txId,
                    name: batch.name,
                    origin: batch.origin,
                    locationId: batch.locationId,
                    locationName: batch.locationName,
                    images: [],
                };
            }
            acc[txId].images = [...acc[txId].images, ...batch.images];
            return acc;
        }, {});

        return Object.values(groupedMap);
    }, [batches]);

    useEffect(() => {
        groupedTransactions.forEach((tx) => {
            const txId = tx.transactionId;
            const isUploading = tx.images.some((img) => img.status === 'uploading' || img.status === 'inFlight');

            if (isUploading) {
                if (timersRef.current.has(txId)) {
                    clearTimeout(timersRef.current.get(txId));
                    timersRef.current.delete(txId);
                }
                setHiddenTxIds((prev) => {
                    if (prev.has(txId)) {
                        const next = new Set(prev);
                        next.delete(txId);
                        return next;
                    }
                    return prev;
                });
            } else {
                if (!timersRef.current.has(txId) && !hiddenTxIdsRef.current.has(txId)) {
                    const timerId = setTimeout(() => {
                        setHiddenTxIds((prev) => new Set([...prev, txId]));
                        timersRef.current.delete(txId);
                    }, AUTO_HIDE_DELAY);
                    timersRef.current.set(txId, timerId);
                }
            }
        });
    }, [groupedTransactions]);

    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            timers.forEach((timerId) => clearTimeout(timerId));
            timers.clear();
        };
    }, []);

    // only uploads done by the browser are lost when AIQ is closed; Macaw images are uploaded by the backend
    const hasBrowserUploads = useMemo(
        () => (browserBatches || []).some((batch) => batch.images.some((img) => img.status === 'uploading' || img.status === 'inFlight')),
        [browserBatches]
    );

    const visibleTransactions = useMemo(
        () => groupedTransactions.filter((tx) => !hiddenTxIds.has(tx.transactionId)),
        [groupedTransactions, hiddenTxIds]
    );

    if (!isOpen) return null;

    if (defaultState) {
        const defaultContent = (
            <div ref={panelRef} className={`${styles.notificationContainer} ${styles.open} ${styles.defaultPanel}`}>
                <div className={styles.defaultMessage}>
                    Uploaded media is on the <br />{' '}
                    <Link to="/all-events" className={styles.link} target="_blank" rel="noopener noreferrer" title="Click to open in new tab">
                        All Events
                    </Link>{' '}
                    page. Click to view.
                </div>
            </div>
        );
        return ReactDOM.createPortal(defaultContent, document.body);
    }

    if (visibleTransactions.length === 0) return null;

    const notificationContent = (
        <div ref={panelRef} className={`${styles.notificationContainer} ${isOpen ? styles.open : styles.closed}`}>
            <div className={styles.content}>
                <div className={styles.titleRow}>
                    <div className={styles.titleSection}>
                        <div className={styles.title}>Pending uploads</div>
                        {hasBrowserUploads && (
                            <div className={styles.warning}>
                                <WarningIcon />
                                <span>Don&apos;t close or refresh AIQ</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.transactionsList}>
                    {visibleTransactions.map((transaction, index) => (
                        <TransactionItem
                            key={transaction.transactionId || index}
                            transaction={transaction}
                            isLast={index === visibleTransactions.length - 1}
                        />
                    ))}
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(notificationContent, document.body);
};

UploadNotification.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    defaultState: PropTypes.bool.isRequired,
    onClose: PropTypes.func,
    triggerRef: PropTypes.object,
};

export default UploadNotification;
